/**
 * MD-11 LibraryDrawer — 280px drawer thư viện trích dẫn (localStorage MVP).
 *
 * Spec: docs/superpowers/specs/2026-05-11-chat-leftbar-source-upgrade-design.md §3.3
 * DECISIONS: s2026-05-11-CHAT-LEFTBAR-SOURCE-UPGRADE
 *
 * Storage: `asklaw_library` localStorage (qua `lib/library-store.ts`).
 * Server sync: defer (Phase 2+).
 */

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  loadLibrary,
  removeFromLibrary,
  type LibraryItem,
} from '../lib/library-store';
import { effStatusLabel } from '../lib/citation-meta';

const iconProps = {
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconClose(): ReactElement {
  return (
    <svg {...iconProps} width={18} height={18} aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function IconTrash(): ReactElement {
  return (
    <svg {...iconProps} aria-hidden="true">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
    </svg>
  );
}

function IconExternal(): ReactElement {
  return (
    <svg {...iconProps} aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6M10 14L21 3" />
    </svg>
  );
}

export type LibraryDrawerProps = {
  lang: 'vi' | 'en';
  isOpen: boolean;
  onClose: () => void;
  /** Bump to reload list (after add from SourceCard). */
  refreshTick?: number;
};

export function LibraryDrawer({
  lang,
  isOpen,
  onClose,
  refreshTick,
}: LibraryDrawerProps): ReactElement | null {
  const [items, setItems] = useState<LibraryItem[]>([]);

  const reload = useCallback(() => {
    setItems(loadLibrary());
  }, []);

  useEffect(() => {
    if (isOpen) reload();
  }, [isOpen, reload, refreshTick]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleDelete = useCallback(
    (id: string) => {
      const ok = window.confirm(
        lang === 'vi' ? 'Xóa trích dẫn này khỏi thư viện?' : 'Remove from library?'
      );
      if (!ok) return;
      const next = removeFromLibrary(id);
      setItems(next);
    },
    [lang]
  );

  if (!isOpen) return null;

  return (
    <nav
      className="asklaw-drawer asklaw-drawer--library"
      role="navigation"
      aria-label={lang === 'vi' ? 'Thư viện trích dẫn' : 'Citation library'}
    >
      <div className="asklaw-drawer__header">
        <h2 className="asklaw-drawer__title">
          {lang === 'vi' ? 'Thư viện' : 'Library'}
        </h2>
        <button
          type="button"
          className="asklaw-drawer__close"
          onClick={onClose}
          aria-label={lang === 'vi' ? 'Đóng' : 'Close'}
        >
          <IconClose />
        </button>
      </div>

      <div className="asklaw-drawer__scroll">
        {items.length === 0 ? (
          <div className="asklaw-drawer__hint asklaw-drawer__empty">
            <p>
              {lang === 'vi'
                ? 'Lưu trích dẫn bạn gặp thường xuyên để tra cứu nhanh.'
                : 'Save citations you reference often for quick lookup.'}
            </p>
            <p className="asklaw-drawer__hint-small">
              {lang === 'vi'
                ? 'Nhấn biểu tượng bookmark trên thẻ nguồn để lưu.'
                : 'Tap the bookmark icon on a source card to save.'}
            </p>
          </div>
        ) : (
          <ul className="asklaw-drawer__list" role="list">
            {items.map((it) => {
              const eff = effStatusLabel(it.eff_status, lang);
              return (
                <li key={it.id} className="asklaw-drawer__lib-item" role="listitem">
                  <div className="asklaw-drawer__lib-title">
                    {it.doc_name || it.doc_number || it.doc_id}
                  </div>
                  <div className="asklaw-drawer__lib-meta">
                    <span>{it.provision_path}</span>
                    {it.eff_status && (
                      <span
                        className="asklaw-drawer__lib-badge"
                        style={{ color: eff.color, borderColor: eff.color }}
                      >
                        {eff.label}
                      </span>
                    )}
                  </div>
                  <div className="asklaw-drawer__lib-actions">
                    {it.source_url && (
                      <a
                        href={it.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="asklaw-drawer__lib-link"
                        aria-label={lang === 'vi' ? 'Mở văn bản gốc' : 'Open source'}
                      >
                        <IconExternal />
                        <span>{lang === 'vi' ? 'Mở' : 'Open'}</span>
                      </a>
                    )}
                    <button
                      type="button"
                      className="asklaw-drawer__lib-del"
                      onClick={() => handleDelete(it.id)}
                      aria-label={lang === 'vi' ? 'Xóa trích dẫn' : 'Remove citation'}
                    >
                      <IconTrash />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </nav>
  );
}
